const ENTRY_COLORS = {
  BUY: '#22C55E',
  SELL: '#EF4444',
  UNKNOWN: '#F59E0B',
};

const optionalPriceKeys = {
  entry: ['price_open', 'open_price', 'entry_price', 'entryPrice', 'entry'],
  stopLoss: ['sl', 'stop_loss', 'stopLoss'],
  takeProfit: ['tp', 'take_profit', 'takeProfit', 'profit_target', 'profitTarget'],
};

export function normalizeChartSymbol(symbol) {
  const raw = String(symbol || '').trim().toUpperCase();
  if (!raw) return '';
  return raw.replace(/\s+/g, '');
}

function compactChartSymbol(symbol) {
  const normalized = normalizeChartSymbol(symbol);
  if (!normalized) return '';

  const noSeparators = normalized.replace(/[/_-]/g, '');
  const [withoutBrokerSuffix] = noSeparators.split('.');
  return withoutBrokerSuffix || noSeparators;
}

export function symbolsMatchForChart(left, right) {
  const a = normalizeChartSymbol(left);
  const b = normalizeChartSymbol(right);
  if (!a || !b) return false;
  if (a === b) return true;
  return compactChartSymbol(a) === compactChartSymbol(b);
}

function firstDefined(position, keys) {
  for (const key of keys) {
    if (position?.[key] !== undefined && position?.[key] !== null && position?.[key] !== '') {
      return position[key];
    }
  }
  return null;
}

function parsePositivePrice(value) {
  if (value === null || value === undefined || value === '' || value === 'N/A') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function normalizeDirection(position) {
  const raw = position?.direction ?? position?.side;
  if (typeof raw === 'string' && raw.trim()) {
    const value = raw.trim().toUpperCase();
    if (value.includes('BUY') || value === 'LONG') return 'BUY';
    if (value.includes('SELL') || value === 'SHORT') return 'SELL';
  }
  if (typeof position?.position_type === 'number') {
    return position.position_type === 1 ? 'SELL' : 'BUY';
  }
  return 'UNKNOWN';
}

function getPositionSymbol(position) {
  return position?.symbol ?? position?.instrument ?? position?.instrument_symbol ?? '';
}

function getPositionKey(position, index) {
  return String(
    position?.ticket ??
    position?.position_id ??
    position?.positionId ??
    position?.trade_id ??
    position?.id ??
    `${getPositionSymbol(position) || 'position'}-${index}`
  );
}

function formatVolume(position) {
  const raw = position?.volume ?? position?.lot_size ?? position?.lotSize;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return '';
  return ` ${parsed.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatTicket(position) {
    const raw = position?.ticket ?? position?.position_id ?? position?.positionId ?? position?.id;
    if (raw === undefined || raw === null || raw === '') return '';
    const id = String(raw);
    return ` #${id.length > 4 ? id.slice(-4) : id}`;
}

function formatOverlayPrice(price) {
  const parsed = Number(price);
  if (!Number.isFinite(parsed)) return '';
  return parsed.toLocaleString(undefined, {
    useGrouping: false,
    maximumFractionDigits: 10,
  });
}

function formatOverlaySymbol(position, fallbackSymbol) {
  const raw = getPositionSymbol(position) || fallbackSymbol;
  return normalizeChartSymbol(raw);
}

function shouldSkipPosition(position) {
  const type = typeof position?.type === 'string' ? position.type.toLowerCase() : '';
  return type === 'pending_order' || type === 'order';
}

function normalizeTimeSeconds(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value > 1000000000000 ? Math.floor(value / 1000) : Math.floor(value);
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed)) return normalizeTimeSeconds(parsed);
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

function getPositionOpenTime(position) {
  return firstDefined(position, [
    'time',
    'open_time',
    'openTime',
    'opened_at',
    'openedAt',
    'created_at',
    'createdAt',
    'entry_time',
    'entryTime',
    'open_timestamp',
    'openTimestamp',
    'time_msc',
  ]);
}

function getBarRealTime(bar) {
  return normalizeTimeSeconds(bar?.time_real ?? bar?.time);
}

function getBarChartTime(bar) {
  return normalizeTimeSeconds(bar?.time);
}

function medianInterval(values) {
  const intervals = [];
  for (let i = 1; i < values.length; i += 1) {
    const diff = values[i] - values[i - 1];
    if (diff > 0) intervals.push(diff);
  }
  if (!intervals.length) return 86400;
  intervals.sort((a, b) => a - b);
  return intervals[Math.floor(intervals.length / 2)];
}

export function snapPositionTimeToLoadedBar(time, bars = []) {
  const target = normalizeTimeSeconds(time);
  if (target === null || !Array.isArray(bars) || bars.length === 0) return null;

  const indexedBars = bars
    .map((bar) => ({
      realTime: getBarRealTime(bar),
      chartTime: getBarChartTime(bar),
    }))
    .filter((bar) => bar.realTime !== null && bar.chartTime !== null)
    .sort((a, b) => a.realTime - b.realTime);

  if (!indexedBars.length) return null;

  const realTimes = indexedBars.map((bar) => bar.realTime);
  const interval = medianInterval(realTimes);
  const first = indexedBars[0];
  if (target < first.realTime) {
    return first.realTime - target <= interval * 1.5 ? first.chartTime : null;
  }

  for (let i = 1; i < indexedBars.length; i += 1) {
    if (indexedBars[i].realTime > target) return indexedBars[i - 1].chartTime;
  }

  const last = indexedBars[indexedBars.length - 1];
  return target - last.realTime <= interval * 1.5 ? last.chartTime : null;
}

function getLatestLoadedBar(bars = []) {
  if (!Array.isArray(bars) || bars.length === 0) return null;
  return [...bars]
    .map((bar) => ({
      chartTime: getBarChartTime(bar),
      realTime: getBarRealTime(bar),
      close: parsePositivePrice(bar?.close),
    }))
    .filter((bar) => bar.chartTime !== null && bar.realTime !== null)
    .sort((a, b) => a.realTime - b.realTime)
    .at(-1) || null;
}

function getPositionAccount(position) {
  return position?.accountId ?? position?.account_id ?? position?.account ?? null;
}

function accountMatchesForChart(position, selectedAccount) {
  const positionAccount = getPositionAccount(position);
  return !selectedAccount || positionAccount === null || String(positionAccount) === String(selectedAccount);
}

function formatProfit(position) {
  const parsed = Number(position?.profit ?? position?.unrealized_pnl ?? position?.pnl);
  if (!Number.isFinite(parsed)) return '';
  const sign = parsed > 0 ? '+' : '';
  return ` ${sign}${parsed.toFixed(2)}`;
}

function connectorColor(position, direction) {
  const parsed = Number(position?.profit ?? position?.unrealized_pnl ?? position?.pnl);
  if (Number.isFinite(parsed)) {
    if (parsed > 0) return '#22C55E';
    if (parsed < 0) return '#EF4444';
  }
  return ENTRY_COLORS[direction] || ENTRY_COLORS.UNKNOWN;
}

export function getOpenPositionOverlaySpecs(openPositions, selectedSymbol) {
  if (!Array.isArray(openPositions) || !selectedSymbol) return [];

  const specs = [];
  openPositions.forEach((position, index) => {
    if (!position || typeof position !== 'object' || shouldSkipPosition(position)) return;
    if (!symbolsMatchForChart(getPositionSymbol(position), selectedSymbol)) return;

    const key = getPositionKey(position, index);
    const direction = normalizeDirection(position);
    const symbolLabel = formatOverlaySymbol(position, selectedSymbol);
    const ticketLabel = formatTicket(position);
    const volumeLabel = formatVolume(position);
    const entryPrice = parsePositivePrice(firstDefined(position, optionalPriceKeys.entry));
    const stopLoss = parsePositivePrice(firstDefined(position, optionalPriceKeys.stopLoss));
    const takeProfit = parsePositivePrice(firstDefined(position, optionalPriceKeys.takeProfit));

    if (entryPrice !== null) {
      specs.push({
        key: `${key}:entry`,
        kind: 'entry',
        price: entryPrice,
        color: ENTRY_COLORS[direction] || ENTRY_COLORS.UNKNOWN,
        title: `${symbolLabel} ${direction === 'UNKNOWN' ? 'POS' : direction}${volumeLabel}${ticketLabel} @ ${formatOverlayPrice(entryPrice)}`,
      });
    }

    if (stopLoss !== null) {
      specs.push({
        key: `${key}:sl`,
        kind: 'stopLoss',
        price: stopLoss,
        color: '#EF4444',
        title: `${symbolLabel} SL${ticketLabel} @ ${formatOverlayPrice(stopLoss)}`,
      });
    }

    if (takeProfit !== null) {
      specs.push({
        key: `${key}:tp`,
        kind: 'takeProfit',
        price: takeProfit,
        color: '#22C55E',
        title: `${symbolLabel} TP${ticketLabel} @ ${formatOverlayPrice(takeProfit)}`,
      });
    }
  });

  return specs;
}

export function buildOpenPositionTradeOverlay(openPositions, selectedSymbol, bars = [], selectedAccount = null) {
  if (!Array.isArray(openPositions) || !selectedSymbol) return { markers: [], connectors: [] };

  const markers = [];
  const connectors = [];
  const latestBar = getLatestLoadedBar(bars);

  openPositions.forEach((position, index) => {
    if (!position || typeof position !== 'object' || shouldSkipPosition(position)) return;
    if (!accountMatchesForChart(position, selectedAccount)) return;
    if (!symbolsMatchForChart(getPositionSymbol(position), selectedSymbol)) return;

    const entryPrice = parsePositivePrice(firstDefined(position, optionalPriceKeys.entry));
    const entryTime = snapPositionTimeToLoadedBar(getPositionOpenTime(position), bars);
    if (entryPrice === null || entryTime === null) return;

    const key = getPositionKey(position, index);
    const direction = normalizeDirection(position);
    const isBuy = direction === 'BUY';
    const idSuffix = formatTicket(position);
    const volumeLabel = formatVolume(position);
    const entryPriceText = formatOverlayPrice(entryPrice);
    const profitText = formatProfit(position);

    markers.push({
      id: `open:${key}:${entryTime}`,
      time: entryTime,
      position: isBuy ? 'atPriceBottom' : 'atPriceTop',
      price: entryPrice,
      color: ENTRY_COLORS[direction] || ENTRY_COLORS.UNKNOWN,
      shape: isBuy ? 'arrowUp' : 'arrowDown',
      text: `OPEN ${direction === 'UNKNOWN' ? 'POS' : direction}${volumeLabel}${idSuffix} @ ${entryPriceText}${profitText}`,
    });

    const currentPrice = parsePositivePrice(firstDefined(position, [
      'price_current',
      'current_price',
      'currentPrice',
      'market_price',
      'marketPrice',
    ])) ?? latestBar?.close;
    if (latestBar && latestBar.chartTime !== entryTime && currentPrice !== null) {
      const data = [
        { time: entryTime, value: entryPrice },
        { time: latestBar.chartTime, value: currentPrice },
      ].sort((a, b) => a.time - b.time);
      connectors.push({
        id: `open:${key}:connector`,
        color: connectorColor(position, direction),
        lineStyle: 'dashed',
        data,
      });
    }
  });

  return {
    markers: markers.sort((a, b) => a.time - b.time),
    connectors,
  };
}
